import { useState, useEffect, useRef } from 'react';
import {
  getWizardClientSessionId,
  getLastWizardSearchRequestId,
} from '../utils/wizardClientSession';
import {
  fetchWizardSearchRequestForViewer,
  linkWizardSearchRequestToAccountIfNeeded,
  mapServiceWizardSearchRequestToWizardState,
} from '../utils/wizardSearchRequestLink';

export { mapServiceWizardSearchRequestToWizardState };

/**
 * When router state has no `wizard`, load wizard payload from GET by id.
 * Signed-in: links session-owned docs to the account (when clientSessionId is known), then GET.
 * Guest: when client session id and active search id match `wizardSearchRequestId`.
 *
 * @param {boolean} userLoggedIn
 * @param {string | null | undefined} wizardSearchRequestId
 * @param {object | null | undefined} routerWizard — `location.state?.wizard`; when set, hook clears API cache
 * @param {{ linkClientSessionId?: string | null }} [options]
 * @returns {{ wizardState: object | null, loading: boolean }}
 */
export function useWizardStateFromSearchRequestApi(
  userLoggedIn,
  wizardSearchRequestId,
  routerWizard,
  options = {},
) {
  const { linkClientSessionId = null } = options;
  const [apiWizardState, setApiWizardState] = useState(null);
  const [loading, setLoading] = useState(
    () => !routerWizard && Boolean(wizardSearchRequestId),
  );
  const linkedRequestIdsRef = useRef(new Set());

  useEffect(() => {
    if (routerWizard) {
      setApiWizardState(null);
      setLoading(false);
      return undefined;
    }
    if (!wizardSearchRequestId) {
      setApiWizardState(null);
      setLoading(false);
      return undefined;
    }
    if (!userLoggedIn) {
      const sid = getWizardClientSessionId();
      const activeStoredId = getLastWizardSearchRequestId();
      if (!sid || !activeStoredId || activeStoredId !== wizardSearchRequestId) {
        setApiWizardState(null);
        setLoading(false);
        return undefined;
      }
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const sessionIdForLink =
          (typeof linkClientSessionId === 'string' && linkClientSessionId.trim()) ||
          getWizardClientSessionId();

        if (
          userLoggedIn &&
          sessionIdForLink &&
          !linkedRequestIdsRef.current.has(wizardSearchRequestId)
        ) {
          await linkWizardSearchRequestToAccountIfNeeded(
            wizardSearchRequestId,
            sessionIdForLink,
          );
          linkedRequestIdsRef.current.add(wizardSearchRequestId);
        }

        const doc = await fetchWizardSearchRequestForViewer(wizardSearchRequestId, {
          userLoggedIn,
          clientSessionId: userLoggedIn ? undefined : sessionIdForLink,
        });
        if (cancelled) return;
        const mapped = doc ? mapServiceWizardSearchRequestToWizardState(doc) : null;
        setApiWizardState(mapped);
      } catch (e) {
        console.error('useWizardStateFromSearchRequestApi:', e);
        if (!cancelled) {
          setApiWizardState(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userLoggedIn, wizardSearchRequestId, routerWizard, linkClientSessionId]);

  return { wizardState: apiWizardState, loading };
}
